/**
 * Control Plane Mapper
 *
 * Pure conversion functions between UI types (controlPlane/types.ts)
 * and Supabase DB row shapes (cp_* tables).
 */

import type {
  ManagedUser, ManagedTenant, ControlPlaneAuditEntry,
  BehaviorMode, UserAccountStatus, TenantStatus,
  AuditActionType, AuditScope, ModeProfileId, Environment,
} from "@/data/controlPlane/types";
import type { UserRole } from "@/data/RoleContext";

// ── DB row shapes ─────────────────────────────────────────────────────────────

export interface CpSystemSettingsRow {
  id: string;
  environment: string;
  mode_profile: string;
  updated_at?: string;
}

export interface CpCapabilityOverrideRow {
  capability_id: string;
  enabled: boolean;
  behavior_mode: string;
  updated_at?: string;
}

export interface CpAuditLogRow {
  id: string;
  timestamp: string;
  actor: string;
  actor_role: string;
  action_type: string;
  target_type: string;
  target_id: string;
  capability_id: string | null;
  old_value: Record<string, unknown>;
  new_value: Record<string, unknown>;
  reason: string;
  scope: string;
  created_at?: string;
}

export interface CpUserRow {
  id: string;
  email: string;
  display_name: string;
  first_name: string;
  last_name: string;
  role: string;
  status: string;
  tenant_id: string;
  department: string | null;
  team: string | null;
  avatar_url: string | null;
  invited_at: string | null;
  activated_at: string | null;
  last_login_at: string | null;
  last_activity_at: string | null;
  capability_overrides: Record<string, { enabled: boolean; behaviorMode: BehaviorMode }>;
  metadata: Record<string, string>;
  created_at: string;
  updated_at: string;
}

export interface CpTenantRow {
  id: string;
  name: string;
  slug: string;
  status: string;
  mode_profile: string;
  environment: string;
  user_count: number;
  capability_overrides: Record<string, { enabled: boolean; behaviorMode: BehaviorMode }>;
  metadata: Record<string, string>;
  created_at: string;
  updated_at: string;
}

// ── System settings mappers ───────────────────────────────────────────────────

export function toDbSystemSettings(
  environment: Environment,
  modeProfile: ModeProfileId,
): Omit<CpSystemSettingsRow, "updated_at"> {
  return { id: "global", environment, mode_profile: modeProfile };
}

export function toUiSystemSettings(
  row: CpSystemSettingsRow,
): { environment: Environment; modeProfile: ModeProfileId } {
  return {
    environment: row.environment as Environment,
    modeProfile: row.mode_profile as ModeProfileId,
  };
}

// ── Capability override mappers ───────────────────────────────────────────────

export function toDbOverride(
  capabilityId: string,
  enabled: boolean,
  mode: BehaviorMode,
): Omit<CpCapabilityOverrideRow, "updated_at"> {
  return { capability_id: capabilityId, enabled, behavior_mode: mode };
}

export function toUiOverrides(
  rows: CpCapabilityOverrideRow[],
): Record<string, { enabled: boolean; behaviorMode: BehaviorMode }> {
  return Object.fromEntries(
    rows.map(r => [r.capability_id, { enabled: r.enabled, behaviorMode: r.behavior_mode as BehaviorMode }]),
  );
}

// ── Audit log mappers ─────────────────────────────────────────────────────────

export function toDbAuditEntry(entry: ControlPlaneAuditEntry): Omit<CpAuditLogRow, "created_at"> {
  return {
    id: entry.id,
    timestamp: entry.timestamp,
    actor: entry.actor,
    actor_role: entry.actorRole,
    action_type: entry.actionType,
    target_type: entry.targetType,
    target_id: entry.targetId,
    capability_id: entry.capabilityId ?? null,
    old_value: entry.oldValue,
    new_value: entry.newValue,
    reason: entry.reason,
    scope: entry.scope,
  };
}

export function toUiAuditEntry(row: CpAuditLogRow): ControlPlaneAuditEntry {
  return {
    id: row.id,
    timestamp: row.timestamp,
    actor: row.actor,
    actorRole: row.actor_role as UserRole,
    actionType: row.action_type as AuditActionType,
    targetType: row.target_type as ControlPlaneAuditEntry["targetType"],
    targetId: row.target_id,
    capabilityId: row.capability_id ?? undefined,
    oldValue: row.old_value,
    newValue: row.new_value,
    reason: row.reason,
    scope: row.scope as AuditScope,
  };
}

// ── User mappers ──────────────────────────────────────────────────────────────

export function toDbUser(user: ManagedUser): CpUserRow {
  return {
    id: user.id,
    email: user.email,
    display_name: user.displayName,
    first_name: user.firstName,
    last_name: user.lastName,
    role: user.role,
    status: user.status,
    tenant_id: user.tenantId,
    department: user.department ?? null,
    team: user.team ?? null,
    avatar_url: user.avatarUrl ?? null,
    invited_at: user.invitedAt ?? null,
    activated_at: user.activatedAt ?? null,
    last_login_at: user.lastLoginAt ?? null,
    last_activity_at: user.lastActivityAt ?? null,
    capability_overrides: user.capabilityOverrides,
    metadata: user.metadata,
    created_at: user.createdAt,
    updated_at: user.updatedAt,
  };
}

export function toUiUser(row: CpUserRow): ManagedUser {
  return {
    id: row.id,
    email: row.email,
    displayName: row.display_name,
    firstName: row.first_name,
    lastName: row.last_name,
    role: row.role as UserRole,
    status: row.status as UserAccountStatus,
    tenantId: row.tenant_id,
    department: row.department ?? undefined,
    team: row.team ?? undefined,
    avatarUrl: row.avatar_url ?? undefined,
    invitedAt: row.invited_at ?? undefined,
    activatedAt: row.activated_at ?? undefined,
    lastLoginAt: row.last_login_at ?? undefined,
    lastActivityAt: row.last_activity_at ?? undefined,
    capabilityOverrides: row.capability_overrides ?? {},
    metadata: row.metadata ?? {},
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ── Tenant mappers ────────────────────────────────────────────────────────────

export function toDbTenant(tenant: ManagedTenant): CpTenantRow {
  return {
    id: tenant.id,
    name: tenant.name,
    slug: tenant.slug,
    status: tenant.status,
    mode_profile: tenant.modeProfile,
    environment: tenant.environment,
    user_count: tenant.userCount,
    capability_overrides: tenant.capabilityOverrides,
    metadata: tenant.metadata,
    created_at: tenant.createdAt,
    updated_at: tenant.updatedAt,
  };
}

export function toUiTenant(row: CpTenantRow): ManagedTenant {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    status: row.status as TenantStatus,
    modeProfile: row.mode_profile as ModeProfileId,
    environment: row.environment as Environment,
    userCount: row.user_count,
    capabilityOverrides: row.capability_overrides ?? {},
    metadata: row.metadata ?? {},
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}
